import type { ReactNode } from "react";
import { DepartmentFieldAccess } from "../departmentFields";
import { useQuoteCopy } from "../i18n/QuoteCopyContext";
import type { DepartmentConfig, FormState } from "../types";
import { CargoTable } from "./CargoTable";
import { ArrowRightIcon } from "./icons";
import { ServicesSummary } from "./ServicesSummary";
import { ReviewValue, SummaryGrid, SummaryRow, SummarySection } from "./SummarySection";
import { WizardPage } from "./WizardPage";

interface Props {
  form: FormState;
  config: DepartmentConfig;
  submitting: boolean;
  onBack: () => void;
  onConfirm: () => void;
}

class ReviewRouteLine {
  constructor(
    readonly label: string,
    readonly from: ReactNode,
    readonly to: ReactNode,
  ) {}
}

function ReviewRouteField({
  side,
  label,
  value,
}: {
  side: string;
  label: string;
  value: ReactNode;
}) {
  return (
    <div className="nv-review-route-side">
      <div className="summary-label">
        <span className="nv-sr-only">{side} </span>
        {label}
      </div>
      <div className="summary-value">{value}</div>
    </div>
  );
}

function ReviewRouteColumn({ tag, children }: { tag: string; children: ReactNode }) {
  return (
    <div className="nv-review-route-col">
      <div className="nv-route-tag">{tag}</div>
      {children}
    </div>
  );
}

export function ReviewStep({
  form,
  config,
  submitting,
  onBack,
  onConfirm,
}: Props) {
  const copy = useQuoteCopy();
  const fields = new DepartmentFieldAccess(config);
  const showStreet = fields.isPresent("loadStreet");
  const showHouse = fields.isPresent("loadHouseNumber");
  const showUnloadDate = fields.isPresent("unloadDate");
  const origin = copy.t("route.origin");
  const destination = copy.t("route.destination");
  const routeLines: ReviewRouteLine[] = [
    new ReviewRouteLine(
      copy.t("route.country"),
      <ReviewValue value={copy.countryName(form.loadCountry)} />,
      <ReviewValue value={copy.countryName(form.unloadCountry)} />,
    ),
    new ReviewRouteLine(
      copy.t("route.postcode"),
      <ReviewValue value={form.loadPostal} />,
      <ReviewValue value={form.unloadPostal} />,
    ),
    new ReviewRouteLine(
      copy.t("route.city"),
      <ReviewValue value={form.loadCity} />,
      <ReviewValue value={form.unloadCity} />,
    ),
  ];
  if (showStreet) {
    routeLines.push(
      new ReviewRouteLine(
        copy.t("route.street"),
        <ReviewValue value={form.loadStreet} />,
        <ReviewValue value={form.unloadStreet} />,
      ),
    );
  }
  if (showHouse) {
    routeLines.push(
      new ReviewRouteLine(
        copy.t("route.house"),
        <ReviewValue value={form.loadHouseNumber} />,
        <ReviewValue value={form.unloadHouseNumber} />,
      ),
    );
  }
  return (
    <WizardPage
      title={copy.t("page.review")}
      onBack={onBack}
      onContinue={() => {
        if (!submitting) onConfirm();
      }}
      continueLabel={
        <>
          {submitting ? copy.t("nav.sending") : copy.t("nav.sendRequest")}
          <ArrowRightIcon size={16} />
        </>
      }
      continueDisabled={submitting}
    >
      <SummarySection title={copy.t("page.routeSection")}>
        <div className="nv-review-route">
          <ReviewRouteColumn tag={origin}>
            {routeLines.map((line) => (
              <ReviewRouteField key={line.label} side={origin} label={line.label} value={line.from} />
            ))}
          </ReviewRouteColumn>
          <div className="nv-review-route-arrow" aria-hidden="true">
            <ArrowRightIcon size={20} />
          </div>
          <ReviewRouteColumn tag={destination}>
            {routeLines.map((line) => (
              <ReviewRouteField key={line.label} side={destination} label={line.label} value={line.to} />
            ))}
          </ReviewRouteColumn>
        </div>
      </SummarySection>

      <SummarySection title={copy.t("route.orderDetails")}>
        <SummaryGrid>
            <SummaryRow label={copy.t("route.pickupDate")}>
              <ReviewValue value={form.pickupDate} />
            </SummaryRow>
            {showUnloadDate && (
              <SummaryRow label={copy.t("route.unloadDate")}>
                <ReviewValue value={form.unloadDate} />
              </SummaryRow>
            )}
        </SummaryGrid>
      </SummarySection>

      <SummarySection title={copy.t("page.shipment")}>
        <div className="nv-review-ship-meta">
          <SummaryGrid>
            <SummaryRow label={copy.t("shipment.weight")}>
              {form.totalWeight || "0"} kg
            </SummaryRow>
            <SummaryRow label={copy.t("shipment.goods")}>{form.goods}</SummaryRow>
          </SummaryGrid>
        </div>
        <CargoTable cargo={form.cargo} />
        <div className="nv-review-totals">
          <span>{copy.t("shipment.totalLines", { count: form.cargo.length })}</span>
        </div>
      </SummarySection>

      <ServicesSummary form={form} config={config} />

      <SummarySection title={copy.t("page.contactSection")}>
        <SummaryGrid>
          <SummaryRow label={copy.t("contact.name")}>{form.contactName}</SummaryRow>
          <SummaryRow label={copy.t("contact.company")}>{form.company}</SummaryRow>
          <SummaryRow label={copy.t("contact.email")}>{form.email}</SummaryRow>
          <SummaryRow label={copy.t("contact.phone")}>{form.phone}</SummaryRow>
          <SummaryRow label={copy.t("contact.comment")}>{form.comment}</SummaryRow>
        </SummaryGrid>
      </SummarySection>
    </WizardPage>
  );
}
